import { useState, useEffect } from 'react';
import { api } from '../../api/api';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import EditMarketorModal from '../../components/modal/EditMarketorModal';
import CreateMarketorModal from '../../components/modal/CreateMarketorModal';
import ViewMarketorModal from '../../components/modal/ViewMarketorModal';
import MarketorGiveawayAttemptsModal from '../../components/modal/MarketorGiveawayAttemptsModal';
import MarketorCard from '../../components/modal/MarketorCard';

interface Marketor {
  id: number;
  twitch_user_id: string;
  twitch_display_name: string;
  email: string;
  name: string;
  refid: string;
  created_at: string;
  updated_at: string;
}

const ManageTwitchMarketors = () => {
  const [marketors, setMarketors] = useState<Marketor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredMarketors = marketors.filter(marketor => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return (
      (marketor.twitch_display_name && marketor.twitch_display_name.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (marketor.email && marketor.email.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (marketor.refid && marketor.refid.toLowerCase().includes(lowerCaseSearchTerm))
    );
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isGiveawayAttemptsModalOpen, setIsGiveawayAttemptsModalOpen] = useState(false);
  const [currentMarketor, setCurrentMarketor] = useState<Marketor | null>(null);

  const fetchMarketors = async () => {
    try {
      const response = await api.get('/api/all/marketors');
      setMarketors(response.data);
    } catch (error) {
      console.error('Failed to fetch marketors', error);
    }
  };

  const handleCreateMarketor = async (newMarketor: Omit<Marketor, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      await api.post('/api/all/marketors', newMarketor);
      fetchMarketors();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create marketor', error);
    }
  };

  const handleUpdateMarketor = async (id: number, updatedMarketor: Partial<Marketor>) => {
    try {
      await api.patch(`/api/all/marketors/${id}`, updatedMarketor);
      fetchMarketors();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update marketor', error);
    }
  };

  const handleDeleteMarketor = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this marketor?')) {
      try {
        await api.delete(`/api/all/marketors/${id}`);
        fetchMarketors();
      } catch (error) {
        console.error('Failed to delete marketor', error);
      }
    }
  };

  const openEditModal = (marketor: Marketor) => {
    setCurrentMarketor(marketor);
    setIsEditModalOpen(true);
  };

  const openViewModal = (marketor: Marketor) => {
    setCurrentMarketor(marketor);
    setIsViewModalOpen(true);
  };

  const openGiveawayAttemptsModal = (marketor: Marketor) => {
    setCurrentMarketor(marketor);
    setIsGiveawayAttemptsModalOpen(true);
  };

  useEffect(() => {
    fetchMarketors();
  }, []);

  return (
    <div className="w-full h-full flex flex-col bg-[#00000071]">
      <AdminHeader />
      <div
        style={{
          backgroundImage: "url(/assets/img/landing/landing-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="flex-grow flex flex-col items-center justify-center w-full relative pt-6 px-4"
      >
        <div className="w-full max-w-6xl gothic-panel">
          <h1 className="gothic-heading">Manage Twitch Marketors</h1>
          <div className="flex justify-between mb-4">
            <button onClick={() => setIsCreateModalOpen(true)} className="gothic-button gothic-button-primary">
              Add New Marketor
            </button>
            <input
              type="text"
              placeholder="Search marketors..."
              className="gothic-input w-1/3"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMarketors.map((marketor) => (
              <MarketorCard
                key={marketor.id}
                marketor={marketor}
                openViewModal={openViewModal}
                openEditModal={openEditModal}
                handleDeleteMarketor={handleDeleteMarketor}
                openGiveawayAttemptsModal={openGiveawayAttemptsModal}
              />
            ))}
          </div>
        </div>
      </div>
      {isCreateModalOpen && (
        <CreateMarketorModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateMarketor}
        />
      )}
      {isEditModalOpen && currentMarketor && (
        <EditMarketorModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          marketor={currentMarketor}
          onSave={handleUpdateMarketor}
        />
      )}
      {isViewModalOpen && currentMarketor && (
        <ViewMarketorModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          marketor={currentMarketor}
        />
      )}
      {isGiveawayAttemptsModalOpen && currentMarketor && (
        <MarketorGiveawayAttemptsModal
          isOpen={isGiveawayAttemptsModalOpen}
          onClose={() => setIsGiveawayAttemptsModalOpen(false)}
          marketorId={currentMarketor.id}
        />
      )}
      <AdminFooter />
    </div>
  );
};

export default ManageTwitchMarketors;